/**
 * Project + environment dialogs — create, rename, delete.
 *
 * Originally housed inside the (now-retired) TopBar ProjectEnvPicker.
 * They're plain components: open, validate, fire the SDK call, then call
 * `onCreated` / `onSaved` / `onDeleted`. Callers are responsible for
 * navigating to / refreshing the affected record.
 */

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  type CreateEnvironmentBody,
  type CreateProjectBody,
  type EnvironmentRecord,
  type ProjectRecord,
  type UpdateProjectBody,
  WardkeyAPIError,
  useActiveOrg,
  useApiMutation,
} from "@/lib/api";
import { useToast } from "@/lib/toast";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Mirror the API's slug regexes (api/worker/schemas.ts). Client-side validation
// is for instant feedback; the server is the source of truth.
const PROJECT_SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/;
const ENV_SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/;

export function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Portal to <body> so the dim escapes any stacking context our caller
  // happens to live in — without this, a modal opened from inside a
  // `sticky` / `transform` parent (e.g. the sidebar) renders behind the
  // sibling content because z-index doesn't cross stacking contexts.
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      // biome-ignore lint/a11y/useSemanticElements: native <dialog> requires imperative showModal; not worth the rewrite
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-popover p-6 text-popover-foreground shadow-lg">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {title}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
          >
            esc
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

function validateProjectSlug(slug: string): string | null {
  if (slug.length === 0) return "Slug is required.";
  if (slug.length > 64) return "Max 64 characters.";
  if (!PROJECT_SLUG_RE.test(slug)) {
    return "Lowercase letters, digits, and hyphens only — no leading or trailing hyphen.";
  }
  return null;
}

function validateEnvSlug(slug: string): string | null {
  if (slug.length === 0) return "Slug is required.";
  if (slug.length > 32) return "Max 32 characters.";
  if (!ENV_SLUG_RE.test(slug)) {
    return "Lowercase letters, digits, and hyphens only — no leading or trailing hyphen.";
  }
  return null;
}

function extractFieldError(err: WardkeyAPIError | Error): string | null {
  if (err instanceof WardkeyAPIError) {
    if (err.code === "slug_conflict") return "That slug is already in use.";
    if (err.code === "forbidden") return "Owner or admin role required.";
    return err.message;
  }
  return err.message;
}

/** Shared mono-uppercase field eyebrow used across the modal forms. */
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
      {children}
    </span>
  );
}

function FieldError({ children }: { children: React.ReactNode }) {
  return <span className="mt-1.5 block font-mono text-[11px] text-destructive">{children}</span>;
}

export function CreateProjectModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (record: ProjectRecord) => void;
}) {
  const activeOrg = useActiveOrg();
  const { pushToast } = useToast();
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const slugInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    slugInputRef.current?.focus();
  }, []);

  const slugError = touched ? validateProjectSlug(slug) : null;

  const create = useApiMutation<CreateProjectBody, ProjectRecord>(
    (c, body) => c.projects.create(activeOrg.org_id, body),
    {
      suppressToast: true,
      onSuccess: (record) => {
        pushToast({ kind: "success", title: `Created project ${record.project_slug}` });
        onCreated(record);
      },
      onError: (err) => setServerError(extractFieldError(err)),
    },
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    setServerError(null);
    const error = validateProjectSlug(slug);
    if (error) return;
    create.mutate({ slug, display_name: displayName.trim() || slug });
  }

  return (
    <ModalShell title="new project" onClose={onClose}>
      <h2 className="mt-2 text-xl font-medium text-foreground">Create project</h2>
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
        Groups environments. One project per app or service is the norm — e.g.{" "}
        <code className="font-mono text-foreground">api</code>,{" "}
        <code className="font-mono text-foreground">web</code>.
      </p>
      <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
        <label className="block">
          <FieldLabel>slug</FieldLabel>
          <Input
            ref={slugInputRef}
            type="text"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value.toLowerCase());
              setServerError(null);
            }}
            onBlur={() => setTouched(true)}
            placeholder="api"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-invalid={slugError !== null || serverError !== null}
            className="mt-2 font-mono"
          />
          {(slugError || serverError) && <FieldError>{slugError ?? serverError}</FieldError>}
        </label>
        <label className="block">
          <FieldLabel>
            display name <span className="text-muted-foreground/70">(optional)</span>
          </FieldLabel>
          <Input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="API"
            className="mt-2"
          />
        </label>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={create.isPending || validateProjectSlug(slug) !== null}
          >
            {create.isPending ? "Creating…" : "Create project"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

export function CreateEnvModal({
  project,
  onClose,
  onCreated,
}: {
  project: ProjectRecord;
  onClose: () => void;
  onCreated: (record: EnvironmentRecord) => void;
}) {
  const activeOrg = useActiveOrg();
  const { pushToast } = useToast();
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const slugInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    slugInputRef.current?.focus();
  }, []);

  const slugError = touched ? validateEnvSlug(slug) : null;

  const create = useApiMutation<CreateEnvironmentBody, EnvironmentRecord>(
    (c, body) => c.envs.create(activeOrg.org_id, project.project_slug, body),
    {
      suppressToast: true,
      onSuccess: (record) => {
        pushToast({ kind: "success", title: `Created env ${record.env_slug}` });
        onCreated(record);
      },
      onError: (err) => setServerError(extractFieldError(err)),
    },
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    setServerError(null);
    const error = validateEnvSlug(slug);
    if (error) return;
    create.mutate({ slug, display_name: displayName.trim() || slug });
  }

  return (
    <ModalShell title={`new env · ${project.project_slug}`} onClose={onClose}>
      <h2 className="mt-2 text-xl font-medium text-foreground">Create environment</h2>
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
        Scopes secrets within{" "}
        <code className="font-mono text-foreground">{project.project_slug}</code>. Convention:{" "}
        <code className="font-mono text-foreground">staging</code>,{" "}
        <code className="font-mono text-foreground">production</code>.
      </p>
      <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
        <label className="block">
          <FieldLabel>slug</FieldLabel>
          <Input
            ref={slugInputRef}
            type="text"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value.toLowerCase());
              setServerError(null);
            }}
            onBlur={() => setTouched(true)}
            placeholder="staging"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-invalid={slugError !== null || serverError !== null}
            className="mt-2 font-mono"
          />
          {(slugError || serverError) && <FieldError>{slugError ?? serverError}</FieldError>}
        </label>
        <label className="block">
          <FieldLabel>
            display name <span className="text-muted-foreground/70">(optional)</span>
          </FieldLabel>
          <Input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Staging"
            className="mt-2"
          />
        </label>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={create.isPending || validateEnvSlug(slug) !== null}
          >
            {create.isPending ? "Creating…" : "Create env"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

export function EditProjectModal({
  project,
  onClose,
  onSaved,
}: {
  project: ProjectRecord;
  onClose: () => void;
  onSaved: (record: ProjectRecord) => void;
}) {
  const activeOrg = useActiveOrg();
  const { pushToast } = useToast();
  // Slug is immutable on the server, so only display_name is editable here.
  const [displayName, setDisplayName] = useState(project.display_name || project.project_slug);
  const [serverError, setServerError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const save = useApiMutation<UpdateProjectBody, ProjectRecord>(
    (c, body) => c.projects.update(activeOrg.org_id, project.project_slug, body),
    {
      suppressToast: true,
      onSuccess: (record) => {
        pushToast({ kind: "success", title: `Renamed ${record.project_slug}` });
        onSaved(record);
      },
      onError: (err) => setServerError(extractFieldError(err)),
    },
  );

  const trimmed = displayName.trim();
  const isUnchanged = trimmed === (project.display_name || project.project_slug);
  const isEmpty = trimmed.length === 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    if (isEmpty || isUnchanged) return;
    save.mutate({ display_name: trimmed });
  }

  return (
    <ModalShell title={`rename · ${project.project_slug}`} onClose={onClose}>
      <h2 className="mt-2 text-xl font-medium text-foreground">Rename project</h2>
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
        Only the display name changes. The slug{" "}
        <code className="font-mono text-foreground">{project.project_slug}</code> is permanent —
        it's what the SDK, CLI, and existing URLs reference.
      </p>
      <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
        <label className="block">
          <FieldLabel>display name</FieldLabel>
          <Input
            ref={inputRef}
            type="text"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              setServerError(null);
            }}
            placeholder={project.project_slug}
            aria-invalid={serverError !== null}
            className="mt-2"
          />
          {serverError && <FieldError>{serverError}</FieldError>}
        </label>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={save.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || isEmpty || isUnchanged}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

export function DeleteProjectModal({
  project,
  onClose,
  onDeleted,
}: {
  project: ProjectRecord;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const activeOrg = useActiveOrg();
  const { pushToast } = useToast();
  const [confirmText, setConfirmText] = useState("");
  const [envCount, setEnvCount] = useState<number | null>(null);
  const [otherError, setOtherError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const remove = useApiMutation<void, void>(
    (c) => c.projects.remove(activeOrg.org_id, project.project_slug),
    {
      suppressToast: true,
      onSuccess: () => {
        pushToast({ kind: "success", title: `Deleted project ${project.project_slug}` });
        onDeleted();
      },
      onError: (err) => {
        if (err instanceof WardkeyAPIError && err.code === "project_in_use") {
          const details = err.details as { env_count?: number } | undefined;
          setEnvCount(details?.env_count ?? 0);
          setOtherError(null);
          return;
        }
        setEnvCount(null);
        setOtherError(extractFieldError(err));
      },
    },
  );

  const canSubmit = confirmText === project.project_slug && !remove.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setOtherError(null);
    setEnvCount(null);
    remove.mutate();
  }

  return (
    <ModalShell title={`delete · ${project.project_slug}`} onClose={onClose}>
      <h2 className="mt-2 text-xl font-medium text-foreground">Delete this project</h2>
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
        This permanently deletes the project record. Wardkey will refuse if any environments still
        exist — you'll need to remove those (and their secrets) first.
      </p>
      <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
        <label className="block">
          <FieldLabel>type the slug to confirm</FieldLabel>
          <Input
            ref={inputRef}
            type="text"
            value={confirmText}
            onChange={(e) => {
              setConfirmText(e.target.value);
              setOtherError(null);
              setEnvCount(null);
            }}
            placeholder={project.project_slug}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-label={`Type ${project.project_slug} to confirm deletion`}
            className="mt-2 font-mono"
          />
          <span className="mt-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
            expected: <span className="text-muted-foreground">{project.project_slug}</span>
          </span>
        </label>

        {envCount !== null && (
          <div className="rounded-md border border-border bg-muted/50 p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              still in use
            </div>
            <p className="mt-1.5 text-[13px] text-foreground">
              This project still has {envCount} environment{envCount === 1 ? "" : "s"}. Delete each
              one (and any secrets it holds) before deleting the project.
            </p>
          </div>
        )}
        {otherError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-destructive">
              delete failed
            </div>
            <p className="mt-1.5 text-[13px] text-foreground">{otherError}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={remove.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" variant="destructive" size="sm" disabled={!canSubmit}>
            {remove.isPending ? "Deleting…" : "Delete project"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
